import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faFileDownload,
  faCopy,
  faShare,
  faRocket,
  faBrain,
  faPencilAlt,
  faSearch,
  faBullseye,
  faHashtag,
  faChartLine,
  faPalette,
  faEnvelope,
  faVideo,
  faHandshake,
  faExpand,
  faCompress,
  faPlay
} from '@fortawesome/free-solid-svg-icons';

const SummaryContainer = styled.div`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.98) 0%, rgba(249, 250, 251, 0.98) 100%);
  border-radius: 20px;
  padding: 2rem;
  margin: 2rem 0;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.1);
  border: 2px solid rgba(74, 144, 226, 0.2);
  position: relative;
  overflow: hidden;
  backdrop-filter: blur(10px);
  animation: slideIn 0.5s ease-out;

  @keyframes slideIn {
    from {
      opacity: 0;
      transform: translateY(20px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, #4A90E2, #7B1FA2, #FF6D00);
    animation: shimmer 3s infinite;
  }

  @keyframes shimmer {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(100%); }
  }

  .dark-mode & {
    background: linear-gradient(135deg, rgba(30, 30, 30, 0.98) 0%, rgba(40, 40, 40, 0.98) 100%);
    border-color: rgba(187, 134, 252, 0.2);
  }
`;

const SummaryHeader = styled.div`
  text-align: center;
  margin-bottom: 2rem;
`;

const SuccessIcon = styled.div`
  font-size: 3rem;
  color: #4CAF50;
  margin-bottom: 1rem;
  animation: bounce 1s ease-out;

  @keyframes bounce {
    0%, 20%, 50%, 80%, 100% { transform: translateY(0); }
    40% { transform: translateY(-10px); }
    60% { transform: translateY(-5px); }
  }
`;

const Title = styled.h2`
  font-size: 2rem;
  font-weight: 700;
  color: var(--text-color);
  margin-bottom: 0.5rem;
  font-family: var(--font-heading-bold);
`;

const Subtitle = styled.p`
  font-size: 1.1rem;
  color: var(--text-secondary);
  font-weight: 400;
`;

const DeliverableGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(280px, 1fr));
  gap: 1.5rem;
  margin: 2rem 0;
`;

const DeliverableCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  border: 1px solid rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  cursor: pointer;
  position: relative;
  overflow: hidden;

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.12);
    border-color: ${props => props.borderColor || 'rgba(74, 144, 226, 0.4)'};
  }

  &::after {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 4px;
    height: 100%;
    background: ${props => props.borderColor || '#4A90E2'};
  }

  .dark-mode & {
    background: var(--neutral-200);
    border-color: var(--neutral-300);
  }
`;

const CardHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const CardIcon = styled.div`
  width: 40px;
  height: 40px;
  border-radius: 10px;
  background: ${props => props.bgColor}20;
  color: ${props => props.bgColor};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
`;

const CardTitle = styled.h3`
  font-size: 1rem;
  font-weight: 600;
  color: var(--text-color);
  margin: 0;
`;

const CardContent = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
  line-height: 1.5;
  max-height: ${props => props.expanded ? 'none' : '100px'};
  overflow: ${props => props.expanded ? 'visible' : 'hidden'};
  position: relative;
  transition: max-height 0.3s ease;

  ${props => !props.expanded && `
    &::after {
      content: '';
      position: absolute;
      bottom: 0;
      left: 0;
      right: 0;
      height: 30px;
      background: linear-gradient(transparent, white);
    }
  `}

  .dark-mode & {
    ${props => !props.expanded && `
      &::after {
        background: linear-gradient(transparent, var(--neutral-200));
      }
    `}
  }
`;

const ActionButtons = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid rgba(0, 0, 0, 0.08);
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: ${props => props.primary ?
    'linear-gradient(135deg, #4A90E2, #357ABD)' :
    'white'};
  color: ${props => props.primary ? 'white' : 'var(--text-secondary)'};
  border: 1px solid ${props => props.primary ?
    'transparent' :
    'var(--border-color)'};
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.3s ease;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(74, 144, 226, 0.2);
    ${props => !props.primary && `
      background: var(--background-color);
      color: var(--primary-color);
      border-color: var(--primary-color);
    `}
  }

  &:active {
    transform: translateY(0);
  }

  .dark-mode & {
    ${props => !props.primary && `
      background: var(--neutral-200);
      border-color: var(--neutral-300);
      &:hover {
        background: var(--neutral-300);
        border-color: var(--primary-color);
      }
    `}
  }
`;

const BulkActions = styled.div`
  background: linear-gradient(135deg, #f8f9fa, #e9ecef);
  border-radius: 12px;
  padding: 1.5rem;
  margin: 2rem 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 1rem;

  .dark-mode & {
    background: linear-gradient(135deg, var(--neutral-200), var(--neutral-300));
  }
`;

const BulkActionInfo = styled.div`
  flex: 1;
`;

const ActionCount = styled.div`
  font-size: 1.5rem;
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: 0.25rem;
`;

const ActionDescription = styled.div`
  font-size: 0.9rem;
  color: var(--text-secondary);
`;

const ExecuteAllButton = styled.button`
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 2rem;
  background: linear-gradient(135deg, #4CAF50, #45a049);
  color: white;
  border: none;
  border-radius: 50px;
  font-size: 1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: 0 4px 12px rgba(76, 175, 80, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(76, 175, 80, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 1.2rem;
  }
`;

const ExpandButton = styled.button`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: white;
  border: 1px solid var(--border-color);
  border-radius: 6px;
  padding: 0.25rem 0.5rem;
  cursor: pointer;
  color: var(--text-secondary);
  font-size: 0.75rem;
  transition: all 0.2s ease;

  &:hover {
    background: var(--background-color);
    color: var(--primary-color);
  }

  .dark-mode & {
    background: var(--neutral-200);
    border-color: var(--neutral-300);
  }
`;

const FinalSummaryView = ({ deliverables, onActionExecute, onExport }) => {
  const [expandedCards, setExpandedCards] = useState({});
  const [selectedActions, setSelectedActions] = useState([]);

  useEffect(() => {
    // Auto-select high confidence actions
    if (deliverables && deliverables.actions) {
      const autoSelected = deliverables.actions
        .filter(action => action.confidence > 0.8)
        .slice(0, 3); // Auto-select top 3 high confidence actions
      setSelectedActions(autoSelected);
    }
  }, [deliverables]);

  const toggleCardExpansion = (cardId) => {
    setExpandedCards(prev => ({
      ...prev,
      [cardId]: !prev[cardId]
    }));
  };

  const getAgentIcon = (agent) => {
    const icons = {
      strategist: { icon: faBrain, color: '#7B1FA2' },
      copywriter: { icon: faPencilAlt, color: '#FF6D00' },
      seo_specialist: { icon: faSearch, color: '#689F38' },
      ppc_manager: { icon: faBullseye, color: '#00ACC1' },
      social_media_manager: { icon: faHashtag, color: '#F57C00' },
      data_analyst: { icon: faChartLine, color: '#C2185B' },
      designer: { icon: faPalette, color: '#546E7A' },
      email_marketing: { icon: faEnvelope, color: '#00796B' },
      video_producer: { icon: faVideo, color: '#1976D2' },
      client_manager: { icon: faHandshake, color: '#00796B' }
    };
    return icons[agent] || { icon: faCheckCircle, color: '#4A90E2' };
  };

  const handleExecuteAll = async () => {
    if (onActionExecute) {
      await onActionExecute(selectedActions, { parallel: true });
    }
  };

  const handleCopyAll = () => {
    const allContent = deliverables.raw
      .map(d => `${d.agentName}:\n${d.content}`)
      .join('\n\n---\n\n');
    navigator.clipboard.writeText(allContent);
  };

  const handleExport = (format) => {
    if (onExport) {
      onExport(deliverables, format);
    }
  };

  if (!deliverables || deliverables.raw.length === 0) {
    return null;
  }

  const summary = {
    totalDeliverables: deliverables.raw.length,
    actionCount: deliverables.actions ? deliverables.actions.length : 0,
    categories: Object.keys(deliverables)
      .filter(key => Array.isArray(deliverables[key]) && deliverables[key].length > 0)
      .length
  };

  return (
    <SummaryContainer>
      <SummaryHeader>
        <SuccessIcon>
          <FontAwesomeIcon icon={faCheckCircle} />
        </SuccessIcon>
        <Title>הפרויקט הושלם בהצלחה! 🎉</Title>
        <Subtitle>
          הצוות סיים את העבודה - {summary.totalDeliverables} תוצרים ב-{summary.categories} קטגוריות
        </Subtitle>
      </SummaryHeader>

      {/* Action Summary */}
      {deliverables.actions && deliverables.actions.length > 0 && (
        <BulkActions>
          <BulkActionInfo>
            <ActionCount>{deliverables.actions.length} פעולות זמינות</ActionCount>
            <ActionDescription>
              בחרנו עבורך את הפעולות המומלצות ביותר. תוכל לבצע אותן במקביל.
            </ActionDescription>
          </BulkActionInfo>
          <ExecuteAllButton onClick={handleExecuteAll}>
            <FontAwesomeIcon icon={faPlay} />
            בצע פעולות מומלצות
          </ExecuteAllButton>
        </BulkActions>
      )}

      {/* Special display for final deliverable if exists */}
      {deliverables.final && (
        <div style={{
          background: 'linear-gradient(135deg, #f0f9ff, #e0f2fe)',
          border: '2px solid #0284c7',
          borderRadius: '16px',
          padding: '2rem',
          marginBottom: '2rem',
          position: 'relative'
        }}>
          <div style={{
            position: 'absolute',
            top: '-12px',
            left: '20px',
            background: '#0284c7',
            color: 'white',
            padding: '4px 16px',
            borderRadius: '20px',
            fontSize: '0.875rem',
            fontWeight: '600'
          }}>
            סיכום הפרויקט
          </div>

          {/* Copy button for final summary */}
          <button
            onClick={() => {
              navigator.clipboard.writeText(deliverables.final.content);
              alert('הסיכום הועתק ללוח!');
            }}
            style={{
              position: 'absolute',
              top: '1rem',
              right: '1rem',
              background: 'white',
              border: '1px solid #0284c7',
              borderRadius: '8px',
              padding: '0.5rem 1rem',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '0.5rem',
              color: '#0284c7',
              fontSize: '0.875rem',
              fontWeight: '500',
              transition: 'all 0.2s'
            }}
            onMouseOver={(e) => {
              e.currentTarget.style.background = '#0284c7';
              e.currentTarget.style.color = 'white';
            }}
            onMouseOut={(e) => {
              e.currentTarget.style.background = 'white';
              e.currentTarget.style.color = '#0284c7';
            }}
          >
            <FontAwesomeIcon icon={faCopy} />
            העתק סיכום
          </button>

          <div style={{
            fontFamily: 'var(--font-family)',
            lineHeight: '1.8',
            color: 'var(--text-color)',
            marginTop: '1rem',
            userSelect: 'text',  // Allow text selection
            cursor: 'text'
          }}>
            <div dangerouslySetInnerHTML={{
              __html: deliverables.final.content
                .replace(/\*\*(.*?)\*\*/g, '<strong style="color: #0284c7;">$1</strong>')
                .replace(/^### (.*?)$/gm, '<h3 style="color: #0284c7; margin: 1.5rem 0 0.5rem;">$1</h3>')
                .replace(/^## (.*?)$/gm, '<h2 style="color: #1e40af; margin: 2rem 0 1rem;">$1</h2>')
                .replace(/^- (.*?)$/gm, '<li style="margin: 0.5rem 0;">$1</li>')
                .replace(/(<li.*<\/li>)+/g, '<ul style="padding-right: 1.5rem;">$&</ul>')
                .replace(/\n/g, '<br>')
            }} />
          </div>
        </div>
      )}

      {/* Deliverable Cards */}
      <DeliverableGrid>
        {deliverables.raw.filter(d => d.agent !== 'supervisor' && d.agent !== 'client_manager').map((deliverable) => {
          const agentStyle = getAgentIcon(deliverable.agent);
          const isExpanded = expandedCards[deliverable.id];

          // Special highlighting for key deliverables
          const isKeyDeliverable = deliverable.agent === 'copywriter' ||
                                   deliverable.agent === 'strategist' ||
                                   (deliverable.agent === 'client_manager' && deliverable.content.includes('תוצר סופי'));

          return (
            <DeliverableCard
              key={deliverable.id}
              borderColor={agentStyle.color}
              style={{
                ...(isKeyDeliverable && {
                  background: 'linear-gradient(135deg, white, #fafafa)',
                  border: '2px solid ' + agentStyle.color
                })
              }}
            >
              <div style={{
                position: 'absolute',
                top: '1rem',
                right: '1rem',
                display: 'flex',
                gap: '0.5rem'
              }}>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(deliverable.content);
                    alert('התוכן הועתק ללוח!');
                  }}
                  title="העתק תוכן"
                  style={{
                    background: 'white',
                    border: '1px solid var(--border-color)',
                    borderRadius: '6px',
                    padding: '0.25rem 0.5rem',
                    cursor: 'pointer',
                    color: 'var(--text-secondary)',
                    fontSize: '0.75rem',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseOver={(e) => {
                    e.currentTarget.style.background = 'var(--background-color)';
                    e.currentTarget.style.color = 'var(--primary-color)';
                  }}
                  onMouseOut={(e) => {
                    e.currentTarget.style.background = 'white';
                    e.currentTarget.style.color = 'var(--text-secondary)';
                  }}
                >
                  <FontAwesomeIcon icon={faCopy} />
                </button>
                <ExpandButton onClick={() => toggleCardExpansion(deliverable.id)}>
                  <FontAwesomeIcon icon={isExpanded ? faCompress : faExpand} />
                </ExpandButton>
              </div>

              <CardHeader>
                <CardIcon bgColor={agentStyle.color}>
                  <FontAwesomeIcon icon={agentStyle.icon} />
                </CardIcon>
                <CardTitle>{deliverable.agentName}</CardTitle>
              </CardHeader>

              <CardContent expanded={isExpanded}>
                {/* Special formatting for copy content */}
                {deliverable.agent === 'copywriter' ? (
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '1rem',
                    fontFamily: 'Georgia, serif',
                    fontSize: '1.05rem',
                    lineHeight: '1.7'
                  }}>
                    <div dangerouslySetInnerHTML={{
                      __html: deliverable.content
                        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                        .replace(/\n/g, '<br>')
                    }} />
                  </div>
                ) : (
                  deliverable.content
                )}
              </CardContent>

              {deliverable.actions && deliverable.actions.length > 0 && (
                <ActionButtons>
                  {deliverable.actions.slice(0, 2).map((action, idx) => (
                    <ActionButton
                      key={idx}
                      primary={action.confidence > 0.8}
                      onClick={() => onActionExecute && onActionExecute([action])}
                    >
                      <FontAwesomeIcon icon={faBullseye} />
                      {action.label}
                    </ActionButton>
                  ))}
                </ActionButtons>
              )}
            </DeliverableCard>
          );
        })}
      </DeliverableGrid>

      {/* Export Actions */}
      <ActionButtons style={{ justifyContent: 'center', marginTop: '2rem' }}>
        <ActionButton onClick={handleCopyAll}>
          <FontAwesomeIcon icon={faCopy} />
          העתק הכל
        </ActionButton>
        <ActionButton onClick={() => handleExport('pdf')}>
          <FontAwesomeIcon icon={faFileDownload} />
          ייצא כ-PDF
        </ActionButton>
        <ActionButton onClick={() => handleExport('share')}>
          <FontAwesomeIcon icon={faShare} />
          שתף
        </ActionButton>
      </ActionButtons>
    </SummaryContainer>
  );
};

export default FinalSummaryView;